import React, { useState, useEffect, useRef } from 'react';
import './Booking.css';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config/api';
import {
  authHeaders,
  fetchUserStatus,
  jsonHeaders,
  loginUser,
  normalizeEmail,
  parseApiError,
} from '../utils/apiClient';

const SESSION_TYPES = {
  MINI_INSIGHT: { name: 'Mini Insight Session', duration: 15, price: '₹2,500' },
  FOCUSED_INSIGHT: { name: 'Focussed Insight Session', duration: 30, price: '₹5,000' },
  COMPREHENSIVE_READING: { name: 'Comprehensive Reading', duration: 45, price: '₹7,000' },
  DEEP_DIVE_READING: { name: 'Deep Dive Reading', duration: 60, price: '₹9,000' }
};

const Booking = () => {
  const { user, login: saveAuthSession, logout } = useAuth();
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedSessionType, setSelectedSessionType] = useState('MINI_INSIGHT');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    phone: ''
  });
  const [bookingStep, setBookingStep] = useState('book');
  const [otp, setOtp] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState(null);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const lastCheckedEmailRef = useRef('');
  const otpSentForEmailRef = useRef('');

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  useEffect(() => {
    if (selectedDate && selectedSessionType) {
      fetchAvailableSlots();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, selectedSessionType]);

  useEffect(() => {
    if (user) {
      const nameParts = (user.name || '').trim().split(/\s+/);
      setFormData((prev) => ({
        ...prev,
        email: user.email || prev.email,
        firstName: user.firstName || nameParts[0] || prev.firstName,
        lastName: user.lastName || nameParts.slice(1).join(' ') || prev.lastName,
        phone: user.phone || prev.phone
      }));
    }
  }, [user]);

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  const fetchAvailableSlots = async () => {
    if (!selectedDate || !selectedSessionType) {
      return;
    }

    setSlotsLoading(true);
    setSlotsError(null);

    try {
      const url = `${API_BASE}/slots/available?startDate=${selectedDate}&days=7&sessionType=${selectedSessionType}`;
      const response = await fetch(url);

      if (response.ok) {
        const responseText = await response.text();
        let slots;
        try {
          slots = JSON.parse(responseText);
        } catch (parseError) {
          setSlotsError('Invalid JSON response from server');
          return;
        }

        if (!Array.isArray(slots)) {
          setSlotsError('Invalid response format from server');
          setAvailableSlots([]);
          return;
        }

        const formattedSlots = slots.map(slot => {
          let formattedSlot = { ...slot };

          if (Array.isArray(slot.date)) {
            formattedSlot.date = `${slot.date[0]}-${String(slot.date[1]).padStart(2, '0')}-${String(slot.date[2]).padStart(2, '0')}`;
          } else if (typeof slot.date === 'string') {
            formattedSlot.date = slot.date;
          }

          if (Array.isArray(slot.startTime)) {
            formattedSlot.startTime = `${String(slot.startTime[0]).padStart(2, '0')}:${String(slot.startTime[1]).padStart(2, '0')}`;
          } else if (typeof slot.startTime === 'string') {
            formattedSlot.startTime = slot.startTime;
          }

          if (Array.isArray(slot.endTime)) {
            formattedSlot.endTime = `${String(slot.endTime[0]).padStart(2, '0')}:${String(slot.endTime[1]).padStart(2, '0')}`;
          } else if (typeof slot.endTime === 'string') {
            formattedSlot.endTime = slot.endTime;
          }

          return formattedSlot;
        });

        setAvailableSlots(formattedSlots);
      } else {
        const errorText = await response.text();
        setSlotsError(`Server error: ${response.status} - ${errorText}`);
        setAvailableSlots([]);
      }
    } catch (error) {
      setSlotsError('Network error: Unable to fetch slots');
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const getSlotsForDate = (date) => {
    if (!Array.isArray(availableSlots) || availableSlots.length === 0) {
      return [];
    }

    const filtered = availableSlots.filter(slot => slot && slot.date && slot.date === date);

    return filtered.sort((a, b) => {
      if (!a.startTime || !b.startTime) return 0;
      return a.startTime.localeCompare(b.startTime);
    });
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    setSelectedSlot(null);
    setSlotsError(null);
    setBookingStep('book');
    setMessage('');
  };

  const handleSessionTypeChange = (e) => {
    setSelectedSessionType(e.target.value);
    setSelectedSlot(null);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setBookingStep('book');
    setMessage('');
    setOtp('');
  };

  const handleInputChange = (e) => {
    if (e.target.name === 'email') {
      setBookingStep('book');
      setMessage('');
      setOtp('');
      lastCheckedEmailRef.current = '';
    }

    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const sendVerificationOtp = async (email) => {
    const response = await fetch(
      `${API_BASE}/users/resend-otp?email=${encodeURIComponent(email)}`,
      { method: 'POST', headers: jsonHeaders }
    );

    if (!response.ok) {
      throw new Error(await parseApiError(response));
    }
  };

  const lookupEmailStatus = async (email, { autoSendOtp = false } = {}) => {
    const trimmedEmail = email.trim();
    const status = await fetchUserStatus(trimmedEmail);
    lastCheckedEmailRef.current = trimmedEmail;

    if (!status.exists) {
      setBookingStep('register');
      setMessage('No account found for this email. Fill in your details below — we will send a verification code before booking.');
      return status;
    }

    if (!status.emailVerified) {
      setBookingStep('verify');
      if (autoSendOtp && otpSentForEmailRef.current !== trimmedEmail) {
        try {
          await sendVerificationOtp(trimmedEmail);
          otpSentForEmailRef.current = trimmedEmail;
          setMessage('A verification code has been sent to your email. Enter it below to continue booking.');
        } catch (error) {
          setMessage(error.message || 'Could not send verification code. Click Resend OTP to try again.');
        }
      } else {
        setMessage('This email is registered but not verified. Enter your OTP or click Resend OTP.');
      }
      return status;
    }

    setBookingStep('login');
    setMessage('Welcome back! Enter your password to complete the booking.');
    return status;
  };

  const handleEmailBlur = async () => {
    if (user || !selectedSlot || !formData.email || !isValidEmail(formData.email)) {
      return;
    }

    if (formData.email.trim() === lastCheckedEmailRef.current) {
      return;
    }

    setCheckingEmail(true);
    setMessage('');

    try {
      await lookupEmailStatus(formData.email, { autoSendOtp: true });
    } catch (error) {
      setMessage(error.message || 'Could not check email status. Please try again.');
    } finally {
      setCheckingEmail(false);
    }
  };

  const resetBookingForm = () => {
    setSelectedDate('');
    setSelectedSlot(null);
    setAvailableSlots([]);
    setBookingStep('book');
    setOtp('');
    setMessage('');
    lastCheckedEmailRef.current = '';
    otpSentForEmailRef.current = '';
    if (!user) {
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        phone: ''
      });
    }
  };

  const handleAuthFailure = () => {
    logout();
    setBookingStep('login');
    setFormData((prev) => ({ ...prev, password: '' }));
    return 'We could not confirm your booking. Enter your current password and try again.';
  };

  const submitBooking = async (token) => {
    if (!token) {
      return { ok: false, error: 'Please sign in to complete your booking.' };
    }

    const booking = {
      date: selectedDate,
      startTime: selectedSlot.startTime,
      sessionType: selectedSessionType,
    };

    const response = await fetch(`${API_BASE}/slots/book`, {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(booking)
    });

    if (response.ok) {
      await response.json();
      alert(`Booking confirmed for ${selectedDate} at ${formatTime(selectedSlot.startTime)}`);
      resetBookingForm();
      return { ok: true };
    }

    if (response.status === 401) {
      return { ok: false, error: handleAuthFailure() };
    }

    return { ok: false, error: await parseApiError(response) };
  };

  const authenticateAndBook = async (email, password) => {
    const loginResult = await loginUser(normalizeEmail(email), password);
    if (!loginResult.ok) {
      return { ok: false, error: loginResult.error };
    }

    saveAuthSession(loginResult.user, loginResult.token);
    return submitBooking(loginResult.token);
  };

  const handleResendOtp = async () => {
    setResending(true);
    setMessage('');

    try {
      await sendVerificationOtp(formData.email);
      otpSentForEmailRef.current = formData.email.trim();
      setMessage('A new OTP has been sent to your email');
    } catch (error) {
      setMessage(error.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleVerifyAndBook = async () => {
    setLoading(true);
    setMessage('');

    try {
      const verifyResponse = await fetch(
        `${API_BASE}/users/verify-email?email=${encodeURIComponent(formData.email)}&otp=${encodeURIComponent(otp)}`,
        { method: 'POST', headers: jsonHeaders }
      );

      if (!verifyResponse.ok) {
        setMessage(await parseApiError(verifyResponse));
        return;
      }

      if (!formData.password) {
        setBookingStep('login');
        setMessage('Email verified! Enter your password to complete the booking.');
        return;
      }

      setMessage('Email verified! Completing your booking...');
      const result = await authenticateAndBook(formData.email, formData.password);
      if (!result.ok) {
        setMessage(result.error);
      }
    } catch (error) {
      setMessage('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginAndBook = async () => {
    const email = normalizeEmail(user?.email || formData.email);
    if (!email) {
      alert('Please enter your email');
      return;
    }
    if (!formData.password) {
      setMessage('Enter your password to confirm this booking.');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const result = await authenticateAndBook(email, formData.password);
      if (!result.ok) {
        setMessage(result.error);
      }
    } catch (error) {
      setMessage('Sign in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${API_BASE}/users/register`, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          phone: formData.phone
        })
      });

      if (response.ok) {
        setBookingStep('verify');
        otpSentForEmailRef.current = formData.email.trim();
        setMessage('Account created! Enter the verification code sent to your email to complete booking.');
        return;
      }

      const errorMsg = await parseApiError(response);
      const errorLower = errorMsg.toLowerCase();

      if (errorLower.includes('exist') || errorLower.includes('already')) {
        const status = await fetchUserStatus(formData.email);
        if (status.emailVerified) {
          setBookingStep('login');
          setMessage('This email is already registered. Enter your password to book.');
        } else {
          setBookingStep('verify');
          if (otpSentForEmailRef.current !== formData.email.trim()) {
            try {
              await sendVerificationOtp(formData.email);
              otpSentForEmailRef.current = formData.email.trim();
              setMessage('This email is already registered. A verification code has been sent — enter it below.');
            } catch (error) {
              setMessage('This email is already registered but not verified. Click Resend OTP to receive a code.');
            }
          } else {
            setMessage('This email is already registered but not verified. Enter the OTP or click Resend OTP.');
          }
        }
        return;
      }

      setMessage(errorMsg);
    } catch (error) {
      setMessage('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resolveGuestBookingStep = async (email) => {
    await lookupEmailStatus(email, { autoSendOtp: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedSlot || !formData.email) {
      alert('Please fill all required fields');
      return;
    }

    if (bookingStep === 'register') {
      if (!formData.firstName || !formData.lastName || !formData.password) {
        alert('Please fill all required fields');
        return;
      }
      await handleRegister();
      return;
    }

    if (bookingStep === 'verify') {
      if (!otp) {
        alert('Please enter the OTP');
        return;
      }
      await handleVerifyAndBook();
      return;
    }

    if (bookingStep === 'login' || user) {
      await handleLoginAndBook();
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      await resolveGuestBookingStep(formData.email);
    } catch (error) {
      setMessage(error.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const messageType = message.includes('sent') || message.includes('verified') || message.includes('created') || message.includes('Sign in')
    ? 'success'
    : message.includes('No account') ? 'info' : 'error';

  const submitLabel = () => {
    if (loading) {
      if (bookingStep === 'verify') return 'Verifying...';
      if (bookingStep === 'register') return 'Creating account...';
      if (bookingStep === 'login') return 'Signing in...';
      return 'Booking...';
    }
    if (bookingStep === 'verify') return 'Verify & Complete Booking';
    if (bookingStep === 'register') return 'Create Account & Send OTP';
    if (bookingStep === 'login') return 'Sign In & Complete Booking';
    return 'Confirm Booking';
  };

  return (
    <div className="booking-page">
      <Navigation />
      <div className="min-h-screen bg-sand pt-32">
        <div className="booking-container">
        <h1>Book Your Reading</h1>
        <p>Select a service, date and time for your astrology session</p>

        <form onSubmit={handleSubmit} className="booking-form">
          <div className="session-type-section">
            <label>Select Service:</label>
            <select value={selectedSessionType} onChange={handleSessionTypeChange} required>
              {Object.entries(SESSION_TYPES).map(([key, type]) => (
                <option key={key} value={key}>
                  {type.name} ({type.duration} min) - {type.price}
                </option>
              ))}
            </select>
          </div>

          <div className="date-section">
            <label>Select Date:</label>
            <input
              type="date"
              value={selectedDate}
              onChange={handleDateChange}
              min={new Date().toISOString().split('T')[0]}
              required
            />
          </div>

          {selectedDate && (
            <div className="time-section">
              <label>Available Times:</label>
              {slotsLoading ? (
                <p>Loading available slots...</p>
              ) : slotsError ? (
                <p style={{color: 'red'}}>Error: {slotsError}</p>
              ) : (
                <>
                  <div className="time-grid">
                    {getSlotsForDate(selectedDate).map((slot, index) => {
                      const isSelected = selectedSlot && selectedSlot.startTime === slot.startTime;
                      return (
                        <button
                          key={`${slot.date}-${slot.startTime}-${index}`}
                          type="button"
                          className={`time-slot ${isSelected ? 'selected' : ''}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSlotSelect(slot);
                          }}
                        >
                          {slot.startTime && slot.endTime ?
                            `${formatTime(slot.startTime)} - ${formatTime(slot.endTime)}` :
                            'Invalid Time'
                          }
                        </button>
                      );
                    })}
                  </div>
                  {getSlotsForDate(selectedDate).length === 0 && !slotsLoading && (
                    <p>No available slots for this date</p>
                  )}
                </>
              )}
            </div>
          )}

          {selectedSlot && (
            <div className="contact-section">
              <h3>Booking Summary</h3>
              <div className="booking-summary">
                <p><strong>Service:</strong> {SESSION_TYPES[selectedSessionType].name}</p>
                <p><strong>Duration:</strong> {SESSION_TYPES[selectedSessionType].duration} minutes</p>
                <p><strong>Date:</strong> {selectedDate}</p>
                <p><strong>Time:</strong> {formatTime(selectedSlot.startTime)} - {formatTime(selectedSlot.endTime)}</p>
                <p><strong>Price:</strong> {SESSION_TYPES[selectedSessionType].price}</p>
              </div>

              {!user && (
                <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: '0.95rem' }}>
                  An account is required to book. Enter your email — we will register you or send a verification code if needed.
                </p>
              )}

              <input
                type="email"
                name="email"
                placeholder="Email Address *"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleEmailBlur}
                readOnly={user != null || (bookingStep !== 'book' && bookingStep !== 'register')}
                required
              />

              {checkingEmail && (
                <p style={{ margin: '8px 0 0', color: '#64748b', fontSize: '0.9rem' }}>Checking email...</p>
              )}

              {bookingStep === 'register' && (
                <>
                  <p style={{ margin: '12px 0 0', color: '#334155', fontSize: '0.95rem', fontWeight: 600 }}>
                    Create your account
                  </p>
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First Name *"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Last Name *"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="password"
                    name="password"
                    placeholder="Password *"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </>
              )}

              {(bookingStep === 'login' || user) && (
                <>
                  <p style={{ margin: '12px 0 0', color: '#334155', fontSize: '0.95rem', fontWeight: 600 }}>
                    {user ? 'Confirm with your password' : 'Sign in to book'}
                  </p>
                  <input
                    type="password"
                    name="password"
                    placeholder="Password *"
                    value={formData.password}
                    onChange={handleInputChange}
                    autoComplete="current-password"
                    required
                  />
                </>
              )}

              {bookingStep === 'verify' && (
                <>
                  <p style={{ margin: '12px 0 0', color: '#334155', fontSize: '0.95rem', fontWeight: 600 }}>
                    Verify your email
                  </p>
                  <p style={{ margin: 0, color: '#64748b', fontSize: '0.95rem' }}>
                    Enter the verification code sent to <strong>{formData.email}</strong>
                  </p>
                  <input
                    type="text"
                    placeholder="Enter OTP *"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.toUpperCase())}
                    maxLength={10}
                    autoComplete="one-time-code"
                    required
                  />
                  <p style={{ margin: 0, textAlign: 'center', color: '#64748b', fontSize: '0.9rem' }}>
                    Didn't receive the code?{' '}
                    <button
                      type="button"
                      className="text-link-btn"
                      onClick={handleResendOtp}
                      disabled={loading || resending}
                    >
                      {resending ? 'Sending...' : 'Resend OTP'}
                    </button>
                  </p>
                </>
              )}

              {message && (
                <p className={`booking-message ${messageType}`}>{message}</p>
              )}

              <button type="submit" className="book-button" disabled={loading || resending}>
                {submitLabel()}
              </button>
            </div>
          )}
        </form>

        <div className="flex items-center justify-center gap-3 mt-8 p-4 bg-white rounded-lg shadow-md border border-goldSoft/20">
          <svg className="w-8 h-8 text-green-500" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516"/>
          </svg>
          <div className="text-center">
            <a
              href="https://wa.me/919259806072"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-600 hover:text-green-700 text-sm font-semibold underline"
            >
              Ping us on WhatsApp for booking
            </a>
          </div>
        </div>

        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Booking;
